import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import axios from 'axios';
import { DateRangePicker, DateRangeStrings } from './DateRangePicker';
import {
  PieChart, Pie, Cell,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  LineChart, Line,
  Tooltip, Legend, ResponsiveContainer, LabelList
} from 'recharts';

interface ReportColumn {
  field: string;
  label: string;
  width?: number;
  alignment?: string;
  format?: string;
  visible?: boolean;
}

interface ReportParameter {
  name: string;
  label: string;
  type: string;
  required?: boolean;
  default?: string;
  lookupEntity?: string;
}

interface ReportGroup {
  field: string;
  sortDirection?: string;
  subtotals?: { field: string; aggregation: string }[];
}

interface ReportAggregation {
  field: string;
  aggregation: string;
}

interface ReportChart {
  type: 'pie' | 'bar' | 'line';
  categoryColumn: string;
  valueColumns: string[];
}

interface ReportLayout {
  parameters: ReportParameter[];
  columns: ReportColumn[];
  groups: ReportGroup[];
  sort: { field: string; direction: string }[];
  grandTotals: ReportAggregation[];
  chart?: ReportChart | null;
}

interface ReportResult {
  columns: ReportColumn[];
  rows: Record<string, unknown>[];
  totalRowCount: number;
  truncated: boolean;
}

interface LookupItem {
  id: number;
  name: string;
}

interface WidgetStrings extends DateRangeStrings {
  run: string;
  loading: string;
  all: string;
  noData: string;
  truncated: string;
  subtotal: string;
  grandTotal: string;
  excel: string;
  exportFailed: string;
  culture: string;
  chartCollapse: string;
  chartExpand: string;
  tableCollapse: string;
  tableExpand: string;
}

const FALLBACK_STRINGS: WidgetStrings = {
  run: 'Run',
  loading: 'Loading...',
  all: 'All',
  noData: 'No data found.',
  truncated: 'Showing first {0} of {1} rows. Export to Excel for full results.',
  subtotal: 'Subtotal',
  grandTotal: 'Total',
  excel: 'Excel',
  exportFailed: 'Export failed',
  selectPeriod: 'Select period',
  from: 'From',
  to: 'To',
  thisMonth: 'This month',
  lastMonth: 'Last month',
  thisQuarter: 'This quarter',
  lastQuarter: 'Last quarter',
  thisYear: 'This year',
  lastYear: 'Last year',
  culture: 'nl-NL',
  chartCollapse: 'Hide chart',
  chartExpand: 'Show chart',
  tableCollapse: 'Hide table',
  tableExpand: 'Show table'
};

const CHART_COLORS = [
  '#0d6efd', '#6610f2', '#6f42c1', '#d63384', '#dc3545',
  '#fd7e14', '#ffc107', '#198754', '#20c997', '#0dcaf0'
];

function coerceForServer(value: string, type: string): unknown {
  if (value === '' || value == null) return null;
  switch (type) {
    case 'number': return Number(value);
    case 'boolean': return value === 'true';
    default: return value;
  }
}

function aggregate(values: number[], aggregation: string): number {
  if (values.length === 0) return 0;
  switch (aggregation) {
    case 'sum': return values.reduce((a, b) => a + b, 0);
    case 'count': return values.length;
    case 'average': return values.reduce((a, b) => a + b, 0) / values.length;
    case 'min': return Math.min(...values);
    case 'max': return Math.max(...values);
    default: return 0;
  }
}

// Maps legacy enum values and bare specifiers to canonical .NET format strings.
// Mirrors ReportingService.MapLegacyFormat so client and server agree.
function normalizeFormat(format: string | undefined): string {
  if (!format) return '';
  switch (format.toLowerCase()) {
    case 'text': return '';
    case 'number': return 'N2';
    case 'date': return 'd';
    case 'percentage': return 'P1';
    case 'currency': return 'N0';
    case 'n': return 'N2';
    case 'f': return 'F2';
    case 'p': return 'P1';
    default: return format;
  }
}

function toNumber(value: unknown): number {
  if (typeof value === 'number') return value;
  return Number(value);
}

function toDate(value: unknown): Date | null {
  if (value instanceof Date) return value;
  if (typeof value === 'string' && value.length > 0) {
    // ISO strings ('2026-04-17' or '2026-04-17T00:00:00') parse via Date constructor.
    const d = new Date(value.length === 10 ? value + 'T00:00:00' : value);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

// Interprets a Tier-1 .NET format string using Intl.NumberFormat / Intl.DateTimeFormat.
// Supports: N/N0..N6, F/F0..F6, P/P0..P2, d, and custom date patterns built from
// yyyy/yy, MMMM/MMM/MM, dddd/ddd/dd. Everything else falls back to String(value).
function formatNetValue(value: unknown, format: string | undefined, culture: string): string {
  if (value == null) return '';
  const effective = normalizeFormat(format);
  if (!effective) return String(value);

  const numericMatch = /^([NFPnfp])(\d*)$/.exec(effective);
  if (numericMatch) {
    const spec = numericMatch[1].toUpperCase();
    const digitsText = numericMatch[2];
    const digits = digitsText === ''
      ? (spec === 'P' ? 1 : 2)
      : parseInt(digitsText, 10);
    const n = toNumber(value);
    if (!Number.isFinite(n)) return String(value);

    if (spec === 'P') {
      return new Intl.NumberFormat(culture, {
        style: 'percent',
        minimumFractionDigits: digits,
        maximumFractionDigits: digits
      }).format(n);
    }
    return new Intl.NumberFormat(culture, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
      useGrouping: spec === 'N'
    }).format(n);
  }

  if (effective === 'd') {
    const d = toDate(value);
    if (!d) return String(value);
    return new Intl.DateTimeFormat(culture, { dateStyle: 'short' }).format(d);
  }

  // Custom date pattern — any token present triggers tokenised formatting.
  if (/yyyy|yy|MMMM|MMM|MM|dddd|ddd|dd/.test(effective)) {
    const d = toDate(value);
    if (!d) return String(value);
    return formatDatePattern(d, effective, culture);
  }

  return String(value);
}

function formatDatePattern(date: Date, pattern: string, culture: string): string {
  const y = date.getFullYear();
  const m = date.getMonth(); // 0-based
  const day = date.getDate();

  const pad = (n: number) => String(n).padStart(2, '0');
  const monthShort = new Intl.DateTimeFormat(culture, { month: 'short' }).format(date);
  const monthLong = new Intl.DateTimeFormat(culture, { month: 'long' }).format(date);
  const weekdayShort = new Intl.DateTimeFormat(culture, { weekday: 'short' }).format(date);
  const weekdayLong = new Intl.DateTimeFormat(culture, { weekday: 'long' }).format(date);

  // Replace longest tokens first so "MMMM" isn't mistaken for "MMM"+"M" etc.
  const tokens: [RegExp, string][] = [
    [/yyyy/g, String(y)],
    [/yy/g, String(y % 100).padStart(2, '0')],
    [/MMMM/g, monthLong],
    [/MMM/g, monthShort],
    [/MM/g, pad(m + 1)],
    [/dddd/g, weekdayLong],
    [/ddd/g, weekdayShort],
    [/dd/g, pad(day)]
  ];

  return tokens.reduce((acc, [re, repl]) => acc.replace(re, repl), pattern);
}

function formatTruncated(template: string, shown: number, total: number, culture: string): string {
  return template
    .replace('{0}', shown.toLocaleString(culture))
    .replace('{1}', total.toLocaleString(culture));
}

function getColumnFormat(field: string, columns: ReportColumn[]): string | undefined {
  return columns.find(c => c.field === field)?.format;
}

function renderChart(
  chart: ReportChart,
  rows: Record<string, unknown>[],
  columns: ReportColumn[],
  culture: string
): React.ReactElement {
  // Respect column format if set; otherwise fall back to locale-grouped number formatting
  // so thousand separators always appear even when the column has no explicit format.
  const formatChartValue = (value: unknown, field: string): string => {
    const format = getColumnFormat(field, columns);
    if (format) return formatNetValue(value, format, culture);
    const num = typeof value === 'number' ? value : Number(value);
    if (Number.isFinite(num)) return new Intl.NumberFormat(culture).format(num);
    return String(value ?? '');
  };
  const valueFormatter = (value: unknown, dataKey?: string | number) => {
    const field = typeof dataKey === 'string' ? dataKey : chart.valueColumns[0];
    return formatChartValue(value, field);
  };
  const axisFormatter = (value: unknown) => formatChartValue(value, chart.valueColumns[0]);
  const pieLabelFormatter = (value: unknown) => formatChartValue(value, chart.valueColumns[0]);

  switch (chart.type.toLowerCase()) {
    case 'pie':
      return (
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={rows}
              dataKey={chart.valueColumns[0]}
              nameKey={chart.categoryColumn}
              cx="50%" cy="50%"
              outerRadius="80%"
              label={({ value }: { value?: unknown }) => pieLabelFormatter(value)}
            >
              {rows.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Pie>
            <Tooltip formatter={(v: unknown, n: string) => [valueFormatter(v, n), n]} />
            <Legend layout="vertical" verticalAlign="middle" align="right" />
          </PieChart>
        </ResponsiveContainer>
      );

    case 'bar': {
      const truncate = (s: string, max = 18) => s.length > max ? s.slice(0, max - 1) + '…' : s;
      // Recharts' tick prop is loosely typed at the component boundary; cast to a permissive shape.
      const CategoryTick = ((props: { x?: number; y?: number; payload?: { value?: unknown } }) => {
        const { x = 0, y = 0, payload } = props;
        const full = String(payload?.value ?? '');
        return (
          <g transform={`translate(${x},${y})`}>
            <text x={-3} y={0} dy={4} textAnchor="end" fontSize={12} fill="currentColor">
              {truncate(full)}
              <title>{full}</title>
            </text>
          </g>
        );
      }) as unknown as React.ReactElement;
      return (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" tickFormatter={axisFormatter} />
            <YAxis type="category" dataKey={chart.categoryColumn} width={120} tick={CategoryTick} />
            <Tooltip formatter={(v: unknown, n: string) => [valueFormatter(v, n), n]} />
            <Legend layout="vertical" verticalAlign="middle" align="right" />
            {chart.valueColumns.map((col, i) => (
              <Bar key={col} dataKey={col} fill={CHART_COLORS[i % CHART_COLORS.length]}>
                <LabelList dataKey={col} position="right" formatter={(v: unknown) => formatChartValue(v, col)} />
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      );
    }

    case 'line':
      return (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={chart.categoryColumn} />
            <YAxis tickFormatter={axisFormatter} width={90} />
            <Tooltip formatter={(v: unknown, n: string) => [valueFormatter(v, n), n]} />
            <Legend layout="vertical" verticalAlign="middle" align="right" />
            {chart.valueColumns.map((col, i) => (
              <Line key={col} type="monotone" dataKey={col} stroke={CHART_COLORS[i % CHART_COLORS.length]} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      );

    default:
      return <div className="alert alert-warning m-0">Unknown chart type: {chart.type}</div>;
  }
}

type ReportingWidgetProps = {
  reportDefinitionId: number;
  parameterOverrides?: Record<string, unknown>;
  strings: WidgetStrings;
};

const ReportingWidgetComponent: React.FC<ReportingWidgetProps> = ({ reportDefinitionId, parameterOverrides, strings }) => {
  const [layout, setLayout] = useState<ReportLayout | null>(null);
  const [params, setParams] = useState<Record<string, string>>({});
  const [result, setResult] = useState<ReportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [lookups, setLookups] = useState<Record<string, LookupItem[]>>({});
  const [chartCollapsed, setChartCollapsed] = useState(false);
  const [tableCollapsed, setTableCollapsed] = useState(false);

  const lockedNames = useMemo(
    () => new Set(Object.keys(parameterOverrides ?? {})),
    [parameterOverrides]
  );

  // For each "dateRange" parameter `p`, the server also emits synthetic `p_start`/`p_end`
  // date params. We hide those from the render loop — the single date-range picker owns them.
  const hiddenParamNames = useMemo(() => {
    const hidden = new Set<string>();
    if (!layout) return hidden;
    for (const p of layout.parameters) {
      if (p.type === 'dateRange') {
        hidden.add(p.name + '_start');
        hidden.add(p.name + '_end');
      }
    }
    return hidden;
  }, [layout]);

  const buildTypedPayload = useCallback((): Record<string, unknown> => {
    if (!layout) return {};
    const out: Record<string, unknown> = {};
    for (const p of layout.parameters) {
      const raw = params[p.name];
      out[p.name] = coerceForServer(raw ?? '', p.type);
    }
    return out;
  }, [layout, params]);

  useEffect(() => {
    const load = async () => {
      try {
        const { data: def } = await axios.get(`/api/reporting/${reportDefinitionId}/definition`);
        const parsed: ReportLayout = def.layout;
        setLayout(parsed);

        const initial: Record<string, string> = {};
        for (const p of parsed.parameters) {
          const override = parameterOverrides?.[p.name];
          if (override !== undefined && override !== null) {
            initial[p.name] = String(override);
          } else {
            initial[p.name] = p.default ?? '';
          }
        }
        setParams(initial);

        for (const p of parsed.parameters) {
          if (p.type === 'lookup' && p.lookupEntity) {
            const { data: items } = await axios.get<LookupItem[]>(`/api/reporting/lookup/${p.lookupEntity}`);
            setLookups(prev => ({ ...prev, [p.name]: items }));
          }
        }
      } catch (err: unknown) {
        const e = err as { response?: { data?: { error?: string } }; message?: string };
        setError(e.response?.data?.error ?? e.message ?? String(err));
      }
    };
    load();
  }, [reportDefinitionId, parameterOverrides]);

  const executeReport = useCallback(async () => {
    if (!layout) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.post<ReportResult>(
        `/api/reporting/${reportDefinitionId}/execute`,
        buildTypedPayload()
      );
      setResult(data);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      setError(e.response?.data?.error ?? e.message ?? String(err));
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, [reportDefinitionId, buildTypedPayload, layout]);

  // Auto-run on load and re-run on parameter changes. Debounced so fast-typing
  // text inputs don't trigger a burst of queries.
  useEffect(() => {
    if (!layout) return;
    if (Object.keys(params).length === 0) return;
    const allFilled = layout.parameters
      .filter(p => p.required && !hiddenParamNames.has(p.name))
      .every(p => p.type === 'dateRange'
        ? !!(params[p.name + '_start'] && params[p.name + '_end'])
        : !!(params[p.name] && params[p.name] !== ''));
    if (!allFilled) return;

    const handle = window.setTimeout(() => { void executeReport(); }, 400);
    return () => window.clearTimeout(handle);
  }, [params, layout, hiddenParamNames, executeReport]);

  const exportExcel = useCallback(async () => {
    try {
      const response = await axios.post(
        `/api/reporting/${reportDefinitionId}/export`,
        buildTypedPayload(),
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'] as string | undefined;
      link.download = disposition?.match(/filename="?([^";]+)"?/)?.[1] ?? 'report.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      setError(strings.exportFailed + ': ' + (e.response?.data?.error ?? e.message ?? String(err)));
    }
  }, [reportDefinitionId, buildTypedPayload, strings]);

  if (!layout) return loading ? <div>{strings.loading}</div> : null;

  const visibleColumns = layout.columns.filter(c => c.visible !== false);
  const group = layout.groups[0];

  const hasChart = !!layout.chart;
  const hasColumns = layout.columns.length > 0;
  const showChart = hasChart && result && result.rows.length > 0;
  const showTable = hasColumns && result && result.rows.length > 0;
  const chartOnly = hasChart && !hasColumns;
  const showBoth = hasChart && hasColumns && result && result.rows.length > 0;

  const buildRows = (): React.ReactNode => {
    if (!result || result.rows.length === 0) return null;

    const rows = result.rows;

    if (!group) {
      return (
        <>
          {rows.map((row, i) => (
            <tr key={i}>
              {visibleColumns.map(col => (
                <td key={col.field} style={{ textAlign: (col.alignment as React.CSSProperties['textAlign']) ?? 'left' }}>
                  {formatNetValue(row[col.field], col.format, strings.culture)}
                </td>
              ))}
            </tr>
          ))}
        </>
      );
    }

    const grouped = new Map<string, Record<string, unknown>[]>();
    for (const row of rows) {
      const key = String(row[group.field] ?? '');
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(row);
    }

    const elements: React.ReactNode[] = [];
    for (const [groupKey, groupRows] of grouped) {
      elements.push(
        <tr key={`gh-${groupKey}`} style={{ backgroundColor: '#e8f0fe', fontWeight: 600 }}>
          <td colSpan={visibleColumns.length} style={{ padding: '6px 10px' }}>{groupKey}</td>
        </tr>
      );

      for (let i = 0; i < groupRows.length; i++) {
        const row = groupRows[i];
        elements.push(
          <tr key={`r-${groupKey}-${i}`}>
            {visibleColumns.map(col => (
              <td key={col.field} style={{
                textAlign: (col.alignment as React.CSSProperties['textAlign']) ?? 'left',
                paddingLeft: col.field === visibleColumns[0].field ? '24px' : undefined
              }}>
                {formatNetValue(row[col.field], col.format, strings.culture)}
              </td>
            ))}
          </tr>
        );
      }

      if (group.subtotals && group.subtotals.length > 0) {
        elements.push(
          <tr key={`st-${groupKey}`} style={{ backgroundColor: '#f0f4ff', fontWeight: 500, fontStyle: 'italic' }}>
            {visibleColumns.map((col, i) => {
              const subtotal = group.subtotals!.find(s => s.field === col.field);
              if (i === 0 && !subtotal) {
                return <td key={col.field}>{strings.subtotal} {groupKey}</td>;
              }
              if (subtotal) {
                const values = groupRows.map(r => Number(r[col.field] ?? 0));
                return (
                  <td key={col.field} style={{ textAlign: (col.alignment as React.CSSProperties['textAlign']) ?? 'left' }}>
                    {formatNetValue(aggregate(values, subtotal.aggregation), col.format, strings.culture)}
                  </td>
                );
              }
              return <td key={col.field}>{i === 0 ? `${strings.subtotal} ${groupKey}` : ''}</td>;
            })}
          </tr>
        );
      }
    }

    return <>{elements}</>;
  };

  const buildGrandTotals = (): React.ReactNode => {
    if (!result || result.rows.length === 0 || layout.grandTotals.length === 0) return null;

    return (
      <tfoot>
        <tr style={{ backgroundColor: '#d4e4ff', fontWeight: 700, borderTop: '2px solid #999' }}>
          {visibleColumns.map((col, i) => {
            const total = layout.grandTotals.find(t => t.field === col.field);
            if (total) {
              const values = result.rows.map(r => Number(r[col.field] ?? 0));
              return (
                <td key={col.field} style={{ textAlign: (col.alignment as React.CSSProperties['textAlign']) ?? 'left' }}>
                  {formatNetValue(aggregate(values, total.aggregation), col.format, strings.culture)}
                </td>
              );
            }
            return <td key={col.field}>{i === 0 ? strings.grandTotal : ''}</td>;
          })}
        </tr>
      </tfoot>
    );
  };

  const renderTable = () => (
    <table className="table table-data table-striped" style={{ fontSize: '13px' }}>
      <thead>
        <tr>
          {visibleColumns.map(col => (
            <th key={col.field} style={{
              textAlign: (col.alignment as React.CSSProperties['textAlign']) ?? 'left',
              width: col.width ? `${col.width}%` : undefined
            }}>
              {col.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>{buildRows()}</tbody>
      {buildGrandTotals()}
    </table>
  );

  return (
    <div>
      {layout.parameters.length > 0 && (
        <div style={{ display: 'flex', gap: '12px', padding: '8px 0', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          {layout.parameters.filter(p => !hiddenParamNames.has(p.name)).map(p => {
            if (p.type === 'dateRange') {
              const startName = p.name + '_start';
              const endName = p.name + '_end';
              const locked = lockedNames.has(p.name) || lockedNames.has(startName) || lockedNames.has(endName);
              return (
                <DateRangePicker
                  key={p.name}
                  label={p.label}
                  startValue={params[startName] ?? ''}
                  endValue={params[endName] ?? ''}
                  activePreset={params[p.name] ?? ''}
                  strings={strings}
                  disabled={locked}
                  onChange={(preset, start, end) => setParams(prev => ({
                    ...prev,
                    [p.name]: preset,
                    [startName]: start,
                    [endName]: end
                  }))}
                />
              );
            }
            return (
              <div key={p.name}>
                <label className="form-label" style={{ fontSize: '12px', marginBottom: '2px' }}>{p.label}</label>
                {p.type === 'lookup' ? (
                  <select
                    className="form-select form-select-sm"
                    value={params[p.name] ?? ''}
                    disabled={lockedNames.has(p.name)}
                    onChange={e => setParams(prev => ({ ...prev, [p.name]: e.target.value }))}
                  >
                    <option value="">{strings.all}</option>
                    {(lookups[p.name] ?? []).map(item => (
                      <option key={item.id} value={String(item.id)}>{item.name}</option>
                    ))}
                  </select>
                ) : p.type === 'boolean' ? (
                  <input
                    type="checkbox"
                    className="form-check-input"
                    disabled={lockedNames.has(p.name)}
                    checked={params[p.name] === 'true'}
                    onChange={e => setParams(prev => ({ ...prev, [p.name]: String(e.target.checked) }))}
                  />
                ) : (
                  <input
                    type={p.type === 'date' ? 'date' : p.type === 'number' ? 'number' : 'text'}
                    className="form-control form-control-sm"
                    readOnly={lockedNames.has(p.name)}
                    value={params[p.name] ?? ''}
                    onChange={e => setParams(prev => ({ ...prev, [p.name]: e.target.value }))}
                  />
                )}
              </div>
            );
          })}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {loading && (
              <span className="text-muted" style={{ fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                {strings.loading}
              </span>
            )}
            {result && !loading && !chartOnly && (
              <button className="btn btn-outline-primary btn-sm" onClick={exportExcel}>
                <i className="fas fa-download"></i>&nbsp;
                <span>{strings.excel}</span>
              </button>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="alert alert-danger mt-2" style={{ fontSize: '13px' }}>
          {error}
        </div>
      )}

      {result?.truncated && (
        <div className="alert alert-warning mt-2" style={{ fontSize: '13px' }}>
          {formatTruncated(strings.truncated, result.rows.length, result.totalRowCount, strings.culture)}
        </div>
      )}

      {result && result.rows.length === 0 && (
        <div className="text-muted mt-3">{strings.noData}</div>
      )}

      {!chartOnly && !showBoth && showTable && (
        <div style={{ overflowX: 'auto', marginTop: '8px' }}>
          {renderTable()}
        </div>
      )}

      {showBoth && (
        <div className="row mt-2 g-2">
          <div className="col-12">
            {chartCollapsed ? (
              <div className="d-flex justify-content-end">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setChartCollapsed(false)}
                >
                  ⤢ {strings.chartExpand}
                </button>
              </div>
            ) : (
              <>
                {!tableCollapsed && (
                  <div className="d-flex justify-content-end mb-1">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      title={strings.chartCollapse}
                      onClick={() => setChartCollapsed(true)}
                    >
                      ⤡
                    </button>
                  </div>
                )}
                <div style={{ height: '360px' }}>
                  {renderChart(layout.chart!, result.rows, layout.columns, strings.culture)}
                </div>
              </>
            )}
          </div>
          <div className="col-12">
            {tableCollapsed ? (
              <div className="d-flex justify-content-end">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setTableCollapsed(false)}
                >
                  ⤢ {strings.tableExpand}
                </button>
              </div>
            ) : (
              <>
                {!chartCollapsed && (
                  <div className="d-flex justify-content-end mb-1">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      title={strings.tableCollapse}
                      onClick={() => setTableCollapsed(true)}
                    >
                      ⤡
                    </button>
                  </div>
                )}
                <div style={{ overflowX: 'auto' }}>
                  {renderTable()}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {chartOnly && showChart && (
        <div style={{ height: '360px', marginTop: '8px' }}>
          {renderChart(layout.chart!, result.rows, layout.columns, strings.culture)}
        </div>
      )}
    </div>
  );
};

export function registerReportingWidget(containerId: string): void {
  const container = document.getElementById(containerId);
  if (!container) return;

  const reportDefinitionId = parseInt(container.dataset.reportDefinitionId ?? '0');

  const overridesRaw = container.dataset.parameterOverrides ?? '';
  const parameterOverrides = overridesRaw.length > 0 ? JSON.parse(overridesRaw) : undefined;

  const stringsRaw = container.dataset.strings ?? '';
  const strings: WidgetStrings = stringsRaw.length > 0
    ? { ...FALLBACK_STRINGS, ...JSON.parse(stringsRaw) }
    : FALLBACK_STRINGS;

  createRoot(container).render(
    <ReportingWidgetComponent
      reportDefinitionId={reportDefinitionId}
      parameterOverrides={parameterOverrides}
      strings={strings}
    />
  );
}
